import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

const CUSTOM_QUESTION_IDS = [17,18,19]
const QUESTION_MAPPING = {
  1: { text: "What is your current salary (including superannuation) in AUD?", type: "number" },
  2: { text: "What is your salary expectation for this position (including superannuation) in AUD?", type: "number" },
  3: { text: "Do you have the right to work in Australia?", type: "yesno" },
  4: { text: "What is your notice period (in days)?", type: "number" },
  5: { text: "What is your first availability for an interview?", type: "datetime" },
  6: { text: "What is your alternate availability for an interview?", type: "datetime" },
  7: { text: "Are you currently interviewing for any other open roles?", type: "yesno" },
  8: { text: "Can you accept fully remote work arrangements?", type: "yesno" },
  9: { text: "Can you accept hybrid work arrangements?", type: "yesno" },
  10: { text: "Can you accept fully in-person work arrangements?", type: "yesno" },
  11: { text: "What is your current location (city & suburb)?", type: "text" },
  12: { text: "Do you have your own mode of transport?", type: "yesno" },
  13: { text: "What is your hourly rate expectation in AUD?", type: "number" },
  14: { text: "What is your daily rate expectation in AUD?", type: "number" },
  15: { text: "When are you available to start?", type: "date" },
  16: { text: "What is your security clearance level?", type: "text" },
  17: { text: "Are you proficient in __?", type: "yesno" },
  18: { text: "Are you proficient in __?", type: "yesno" },
  19: { text: "Are you proficient in __?", type: "yesno" },
};

function PrescreenPortalPage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [portalInfo, setPortalInfo] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentStep, setCurrentStep] = useState(0);

  const backendURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      setError('Invalid link. Please check the URL sent to you.');
      setLoading(false);
      return;
    }

    fetchPortalInfo(token);
  }, []);

  const fetchPortalInfo = async (token) => {
    try {
      const response = await axios.get(`${backendURL}/v1/prescreen-portal/info?token=${token}`);
      setPortalInfo(response.data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This prescreening link is invalid or has expired.');
      } else if (err.response?.status === 409) {
        setError('You have already completed this prescreening. Thank you!');
      } else {
        setError('Unable to load prescreening. Please try again later or contact the recruiter.');
      }
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [`Q${questionId}`]: value
    }));
  };

  const handleSubmit = async () => {
    const unansweredQuestions = portalInfo.questionIDs.filter(qId => !answers[`Q${qId}`]);
    
    if (unansweredQuestions.length > 0) {
      setError('Please answer all questions before submitting.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      await axios.post(`${backendURL}/v1/prescreen-portal/submit`, {
        token,
        answers
      });

      setSuccess(true);
      setSubmitting(false);
    } catch (err) {
      setError('Failed to submit prescreening. Please try again.');
      setSubmitting(false);
    }
  };

  const renderQuestionInput = (questionId) => {
    const question = QUESTION_MAPPING[questionId];
    let text = question.text;
    if (CUSTOM_QUESTION_IDS.includes(questionId)) {
      const customisationKey = `Q${questionId}Customisation`;
      const customisation = portalInfo?.[customisationKey] ?? "";
      text = text.replace("__", customisation);
    }
    const value = answers[`Q${questionId}`] || '';

    switch (question.type) {
      case 'yesno':
        return (
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">{text}</FormLabel>
            <RadioGroup
              value={value}
              onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        );

      case 'number':
        return (
          <TextField
            fullWidth
            margin="normal"
            label={text}
            type="number"
            value={value}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            InputProps={{ inputProps: { min: 0 } }}
          />
        );

      case 'datetime':
        return (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              label={text}
              value={value ? moment(value) : null}
              onChange={(newValue) => {
                if (newValue) {
                  handleAnswerChange(questionId, newValue.format('YYYY-MM-DD HH:mm:ss'));
                }
              }}
              renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
            />
          </LocalizationProvider>
        );

      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              label={text}
              value={value ? moment(value) : null}
              onChange={(newValue) => {
                if (newValue) {
                  handleAnswerChange(questionId, newValue.format('YYYY-MM-DD'));
                }
              }}
              renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
            />
          </LocalizationProvider>
        );

      default:
        return (
          <TextField
            fullWidth
            margin="normal"
            label={text}
            value={value}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
          />
        );
    }
  };

  const handleNext = () => {
    const currentQuestionId = portalInfo.questionIDs[currentStep];
    if (!answers[`Q${currentQuestionId}`]) {
      setError('Please answer this question before continuing.');
      return;
    }
    setError(null);
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep(prev => prev - 1);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom color="primary">
              Thank You!
            </Typography>
            <Typography variant="body1" paragraph>
              Your prescreening has been submitted successfully.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              The recruiter will review your responses and contact you soon.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error && !portalInfo) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box minHeight="100vh" bgcolor="#f5f5f5" py={4}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center" color="primary">
            Prescreening for {portalInfo?.jobTitle}
          </Typography>
          
          <Typography variant="body1" gutterBottom align="center" color="textSecondary">
            Hi {portalInfo?.candidateName}, please answer the following questions.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stepper activeStep={currentStep} sx={{ mt: 4, mb: 4 }}>
            {portalInfo?.questionIDs.map((qId, index) => (
              <Step key={qId}>
                <StepLabel>Question {index + 1}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 3 }}>
            {currentStep < portalInfo?.questionIDs.length && (
              <>
                <Typography variant="h6" gutterBottom>
                  Question {currentStep + 1} of {portalInfo?.questionIDs.length}
                </Typography>
                {renderQuestionInput(portalInfo?.questionIDs[currentStep])}
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                onClick={handleBack}
                disabled={currentStep === 0}
                variant="outlined"
              >
                Back
              </Button>

              {currentStep === portalInfo?.questionIDs.length - 1 ? (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Submit'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  color="primary"
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            Powered by RecX
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default PrescreenPortalPage;

